import React, { useCallback } from 'react';
import { ContactList } from '../flowVisualiser/components/contact-list/';
import { RemoteFlowVisualization } from './components/remote-flow-visualization';
import { GetContactResultResponse } from '../api/api-client';
import { EmptyState } from '../flowVisualiser/components/empty-state';
import { LogType } from './types/visualizer';
import { InteractionLogsObject } from './types/interaction-log-data';
import { LocalFlowVisualization } from './components/local-flow-visualization';
import { ConfigurationProvider } from './contexts/configuration-context';
import { ViewProvider, useViewContext } from './contexts/view-context';
import { LogNavigationProvider } from './contexts/log-navigation-context';

const VisualizerPageContent = () => {
  const { currentContactId, fileData, selectContact, loadFileData, closeContact } = useViewContext();

  const handleSelection = useCallback(
    (type: LogType) => {
      if (type === LogType.FLOW_LOG) {
        return;
      }

      if (type === LogType.INTERACTION_LOG) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
              try {
                const jsonData = JSON.parse(e.target?.result as string) as InteractionLogsObject;

                const contactResult: GetContactResultResponse = {
                  interactionLogsObject: jsonData,
                  contact: {
                    Id: jsonData.ContactId,
                    InitiationTimestamp: new Date(jsonData.InteractionLogs[0].AbsoluteTime)
                  },
                  wisdomMessages: []
                };

                loadFileData(contactResult);
              } catch (error) {
                // Do nothing on error as per requirements
              }
            };
            reader.readAsText(file);
          }
        };
        input.click();
      }
    },
    [loadFileData]
  );

  return (
    <div className='font-sans flex flex-col h-full'>
      <div className='flex h-[calc(100vh-48px)] overflow-hidden'>
        <div className='flex flex-col h-full'>
          <ContactList onContactSelect={selectContact} selectedContactId={currentContactId} />
        </div>
        <div className='flex-1 overflow-hidden'>
          {!currentContactId && !fileData && <EmptyState onSelection={handleSelection} />}
          {fileData && (
            <LocalFlowVisualization key={fileData.contact.Id} contactResult={fileData} onClose={closeContact} />
          )}
          {!fileData && currentContactId && (
            <RemoteFlowVisualization contactId={currentContactId} onClose={closeContact} />
          )}
        </div>
      </div>
    </div>
  );
};

export const VisualizerPage = () => {
  return (
    <ViewProvider>
      <ConfigurationProvider>
        <LogNavigationProvider>
          <VisualizerPageContent />
        </LogNavigationProvider>
      </ConfigurationProvider>
    </ViewProvider>
  );
};
